import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { chatService, Chat } from '@/services/chat'
import { useAuthStore } from '@/store/auth'
import {
  ChatBubbleLeftRightIcon,
  ClockIcon,
  TrashIcon,
  SparklesIcon,
  ArrowLeftIcon,
  CalendarIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

export function ChatHistoryPage() {
  const [chats, setChats] = useState<Chat[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const { user, token } = useAuthStore()

  useEffect(() => {
    async function fetchChats() {
      if (!user) {
        setError('You must be logged in to view chat history.')
        setLoading(false)
        return
      }
      try {
        const chatHistory = await chatService.getChats()
        setChats(chatHistory)
      } catch (err) {
        setError('Failed to fetch chat history.')
      } finally {
        setLoading(false)
      }
    }
    fetchChats()
  }, [user])

  const handleDeleteChat = async (chatId: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    setDeletingId(chatId)
    try {
      await chatService.deleteChat(token || undefined, chatId)
      setChats(chats.filter(chat => chat.id !== chatId))
      toast.success('Chat deleted successfully')
    } catch (error) {
      toast.error('Failed to delete chat')
    } finally {
      setDeletingId(null)
    }
  }

  const formatDate = (chat: Chat) => {
    const dateString = chat.created_at || chat.createdAt.toISOString()
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - date.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 1) return 'Today'
    if (diffDays === 2) return 'Yesterday'
    if (diffDays <= 7) return `${diffDays - 1} days ago`

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatTime = (chat: Chat) => {
    return chat.updatedAt.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 border-3 border-ember border-t-transparent rounded-full mx-auto mb-4"
          />
          <p className="text-gray-300 text-lg">Loading your chat history...</p>
        </motion.div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md mx-auto p-8"
        >
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <SparklesIcon className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-red-400 mb-2">Oops!</h2>
          <p className="text-gray-300">{error}</p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-obsidian p-6 relative overflow-hidden">
      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute top-1/4 -left-48 w-96 h-96 bg-ember/10 rounded-full blur-[120px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-48 w-96 h-96 bg-ember/8 rounded-full blur-[100px]"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.15, 0.3, 0.15] }}
          transition={{ duration: 12, repeat: Infinity, delay: 3 }}
        />
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <motion.div whileHover={{ x: -5 }} transition={{ type: 'spring', stiffness: 400 }}>
            <Link
              to="/chat"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-ember-bright mb-6 transition-colors group"
            >
              <ArrowLeftIcon className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              Back to Chat
            </Link>
          </motion.div>

          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-ember to-ember-dim rounded-xl flex items-center justify-center shadow-ember">
              <ChatBubbleLeftRightIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gradient-ember">Chat History</h1>
              <p className="text-gray-400">Review and revisit your conversations</p>
            </div>
          </div>
        </motion.div>

        {chats.length === 0 ? (
          /* Empty State */
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <div className="w-24 h-24 bg-volcanic rounded-full flex items-center justify-center mx-auto mb-6 border border-glass-border">
              <ChatBubbleLeftRightIcon className="w-12 h-12 text-gray-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-300 mb-2">No conversations yet</h3>
            <p className="text-gray-500 mb-6">Start chatting to see your history here</p>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Link
                to="/chat"
                className="inline-flex items-center gap-2 btn-molten px-6 py-3"
              >
                <SparklesIcon className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                Start New Chat
              </Link>
            </motion.div>
          </motion.div>
        ) : (
          /* Chat List */
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-4"
          >
            <AnimatePresence mode="popLayout">
              {chats.map((chat, index) => (
                <motion.div
                  key={chat.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
                  transition={{ delay: index * 0.1 }}
                  layout
                >
                  <Link to={`/chat/${chat.id}`} className="block group">
                    <motion.div
                      className="card-depth p-6 hover:bg-ash transition-all duration-300 hover:border-ember/30"
                      whileHover={{ scale: 1.01, y: -2 }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0 mr-4">
                          {/* Chat Title */}
                          <h3 className="text-lg font-semibold text-white group-hover:text-ember-bright transition-colors mb-2 truncate">
                            {chat.title}
                          </h3>

                          {/* Meta Information */}
                          <div className="flex items-center gap-4 text-sm text-gray-400">
                            <div className="flex items-center gap-1">
                              <CalendarIcon className="w-4 h-4" />
                              <span>{formatDate(chat)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <ClockIcon className="w-4 h-4" />
                              <span>{formatTime(chat)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <motion.button
                            onClick={(e) => handleDeleteChat(chat.id, e)}
                            disabled={deletingId === chat.id}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100"
                          >
                            {deletingId === chat.id ? (
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full"
                              />
                            ) : (
                              <TrashIcon className="w-4 h-4" />
                            )}
                          </motion.button>

                          <div className="w-8 h-8 bg-ember/20 rounded-lg flex items-center justify-center group-hover:bg-ember/30 transition-colors">
                            <ChatBubbleLeftRightIcon className="w-4 h-4 text-ember-bright" />
                          </div>
                        </div>
                      </div>

                      {/* Hover Effect Line */}
                      <motion.div
                        initial={{ scaleX: 0 }}
                        whileHover={{ scaleX: 1 }}
                        className="h-0.5 bg-gradient-to-r from-ember to-ember-dim mt-4 origin-left"
                      />
                    </motion.div>
                  </Link>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </div>
  )
}